import type { CSSProperties } from "react";
import { Icon, type IconName } from "@/components/ui/Icon";
import { useBackups } from "@/hooks/useBackups";
import { fmtBytes, fmtDateLong, relPast } from "@/lib/format";
import { t } from "@/lib/strings";

// Verdict first, file list second: the thing that can be wrong is the thing you
// see first. `configured=false` (mount missing) and `count=0` (no backups) must
// never render alike — the first is a panel bug, the second is an emergency.
//
// The label choice below deliberately keys off `count`, not `newest_at`: the
// scanner can report `newest_at: null` while `count > 0` (every file on disk is
// future-dated or zero-byte, so none of them counts as "fresh"). That is still
// a real backup directory with real files in it — "noch kein Backup vorhanden"
// would be a lie. `backupStale` ("kein aktuelles Backup") is the honest label,
// and it stays red because `is_stale` is true either way.
const rowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  fontSize: 12.5,
  padding: "6px 0",
  borderBottom: "1px solid var(--hairline-soft)",
};

export function BackupStatusSection() {
  const { data, isLoading, isError } = useBackups();

  if (isLoading) return <p style={{ fontSize: 12.5, color: "var(--ink-3)" }}>…</p>;
  if (isError || !data)
    return <p style={{ fontSize: 12.5, color: "var(--danger)" }}>{t.backupLoadError}</p>;

  if (!data.configured) {
    return (
      <div>
        {/* wifi_off stands in for a missing "info" icon in the registry — it
            reads as "not connected", which is exactly what an unmounted
            directory is. Neutral ink, not danger: this is a panel misconfig. */}
        <Verdict icon="wifi_off" color="var(--ink-2)" label={t.backupNotConfigured} />
        <p style={{ margin: "6px 0 0", fontSize: 12, color: "var(--ink-3)" }}>
          {t.backupNotConfiguredHint}
        </p>
      </div>
    );
  }

  const healthy = !data.is_stale;
  const label = healthy ? t.backupOk : data.count === 0 ? t.backupNeverRun : t.backupStale;

  return (
    <div>
      <Verdict
        // "x" stands in for a missing "alertTriangle" icon in the registry —
        // paired with var(--danger) it still reads unambiguously as a failure.
        icon={healthy ? "check" : "x"}
        color={healthy ? "var(--ink-1)" : "var(--danger)"}
        label={label}
      />

      <div style={{ marginTop: 12 }}>
        <div style={rowStyle}>
          <span style={{ color: "var(--ink-3)" }}>{t.backupNewest}</span>
          <span style={{ color: "var(--ink-1)" }}>
            {data.newest_at ? `${fmtDateLong(data.newest_at)} (${relPast(data.newest_at)})` : "—"}
          </span>
        </div>
        <div style={rowStyle}>
          <span style={{ color: "var(--ink-3)" }}>{t.backupCount}</span>
          <span style={{ color: "var(--ink-1)" }}>{data.count}</span>
        </div>
        <div style={rowStyle}>
          <span style={{ color: "var(--ink-3)" }}>{t.backupTotalSize}</span>
          <span style={{ color: "var(--ink-1)" }}>{fmtBytes(data.total_bytes)}</span>
        </div>
      </div>

      {data.files.length > 0 ? (
        <div style={{ marginTop: 12 }}>
          {data.files.map((f) => (
            <div key={f.name} style={rowStyle}>
              <span
                style={{
                  color: "var(--ink-2)",
                  fontFamily: "var(--font-mono, monospace)",
                  fontSize: 12,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {f.name}
              </span>
              <span style={{ color: "var(--ink-3)", flexShrink: 0, paddingLeft: 12 }}>
                {fmtBytes(f.size_bytes)}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Verdict({ icon, color, label }: { icon: IconName; color: string; label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
      {/* Colour carries the status — no emoji. Both fg and bg here are tokens,
          so the pairing stays legible in light and dark. */}
      <Icon name={icon} size={15} color={color} />
      <span style={{ fontSize: 13, fontWeight: 600, color }}>{label}</span>
    </div>
  );
}
